import { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from '@uidotdev/usehooks';
import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';

import { MIN_SEARCH_LENGTH, QUERY_KEYS } from '../constants';
import { SportEventsGrouped } from '../@types/search';
import { getData } from '../utils/api';
import useRecentSearches from '../hooks/useRecentSearches';

const DEBOUNCE_TIMEOUT = 500;

interface SearchContextProps {
  searchValue: string;
  handleSearch: (value: string) => void;
  handleClear: () => void;
  recentSearches: string[];
  saveRecentSearch: (value: string) => void;
  searchResults: SportEventsGrouped[] | undefined;
  isFetching: boolean;
  isError: boolean;
  showSearchBar: boolean;
  setShowSearchBar: (value: boolean) => void;
  hasNoResults: boolean;
}

const SearchContext = createContext<SearchContextProps | undefined>(undefined);

interface SearchProviderProps {
  children: ReactNode;
}

export const SearchProvider = ({ children }: SearchProviderProps) => {
  const { isAuthenticated, userId } = useAuth();
  const { recentSearches, saveRecentSearch, clearRecentSearches } = useRecentSearches();

  const [searchValue, setSearchValue] = useState('');
  const [showSearchBar, setShowSearchBar] = useState(false);

  const debouncedSearchValue = useDebounce(searchValue, DEBOUNCE_TIMEOUT);

  const isSearchValidValue = debouncedSearchValue.trim().length > MIN_SEARCH_LENGTH;

  const handleSearch = useCallback((value: string) => setSearchValue(value), []);
  const handleClear = useCallback(() => setSearchValue(''), []);

  const {
    data: searchResults,
    isFetching,
    isError,
  } = useQuery({
    queryKey: [QUERY_KEYS.searchResults, debouncedSearchValue],
    queryFn: async (): Promise<{ items: SportEventsGrouped[] }> =>
      getData('events/search', {
        search: debouncedSearchValue,
      }),
    select: (data) => data?.items ?? [],
    enabled: isSearchValidValue,
    staleTime: 5 * 60 * 1000,
  });

  const hasNoResults = Boolean(
    isSearchValidValue && !isFetching && !isError && searchResults && searchResults.length === 0
  );

  const prevAuth = useRef({ isAuthenticated, userId });

  useEffect(() => {
    const { isAuthenticated: prevIsAuth, userId: prevUserId } = prevAuth.current;

    if (prevIsAuth && !isAuthenticated && prevUserId) {
      clearRecentSearches(prevUserId);
    }

    prevAuth.current = { isAuthenticated, userId };
  }, [isAuthenticated, userId, clearRecentSearches]);

  const value = useMemo(
    () => ({
      searchValue,
      handleSearch,
      handleClear,
      recentSearches,
      saveRecentSearch,
      searchResults,
      isFetching,
      isError,
      showSearchBar,
      setShowSearchBar,
      hasNoResults,
    }),
    [
      searchValue,
      handleSearch,
      handleClear,
      recentSearches,
      saveRecentSearch,
      searchResults,
      isFetching,
      isError,
      showSearchBar,
      setShowSearchBar,
      hasNoResults,
    ]
  );

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
};

export const useSearchContext = (): SearchContextProps => {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error('useSearchContext must be used within a SearchProvider');
  }
  return context;
};
